import { Box, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import useAPI from "../../../../../../api/hooks/useAPI";
import useColors from "../../../../../../api/hooks/useColors";
import useSmartAndRegularAssets from "../../../../../../api/hooks/useSmartAndRegularAssets";
import { entityKeys } from "../../../../../../api/queryKeys";
import { applySearchFilter } from "../shared/controlEntitiesUtils";
import SearchTextField from "../shared/SearchTextField";
import {
  addToAssetColumn,
  getDisplayNameColumn,
  getExtraInfoColumn,
  getFirstDiscoveredColumn,
  getLabelColumn,
  getThirdInfoColumn,
} from "../shared/sharedColumns";
import EntityTypeTabbedTable from "../tables/EntityTypeTabbedTable";
import EntitiesActionButtons from "./EntitiesActionButtons";

const getTenantColumn = (colors) => {
  return {
    field: "customer_id",
    headerName: "Tenant",
    alignHeader: "left",
    align: "left",
    renderCell: (params) => {
      return (
        <Typography variant="caption">{params.row.customer_id}</Typography>
      );
    },
  };
};

const getRelatedEntitiesTableColumns = (
  entityType,
  entitiesByType,
  colors,
  terminatedEntities,
  hasMultipleSelectedTenants = false
) => {
  const columns = [
    addToAssetColumn,
    getDisplayNameColumn(colors, terminatedEntities),
    getExtraInfoColumn(entityType, colors),
  ];
  const thirdColumn = getThirdInfoColumn(entityType, colors);
  if (thirdColumn) {
    columns.push(thirdColumn);
  }
  columns.push(
    getFirstDiscoveredColumn(colors, (params) => {
      const affectedEntity = entitiesByType[entityType]?.find(
        (ae) => ae.entity_id === params.row.entity_id
      );
      return affectedEntity?.crowdstrike_data?.first_seen?.timestamp_utc || 0;
    })
  );
  if (hasMultipleSelectedTenants) {
    columns.push(getTenantColumn(colors));
  }
  columns.push(getLabelColumn());
  return columns;
};

export default function MisconfiguredEntitiesSection({
  entitiesByType,
  showDetailedAffectedEntities,
  selectedEntityTypeTab,
  setSelectedEntityTypeTab,
  hasMultipleSelectedTenants = false,
}) {
  const colors = useColors();
  const { trackEvent } = useAPI();
  const [searchText, setSearchText] = useState("");
  const [checkedEntityIDs, setCheckedEntityIDs] = useState([]);

  const { regularAndActiveSmartAsset: assets, isLoadingAll: assetsAreLoading } =
    useSmartAndRegularAssets(false);

  const queryClient = useQueryClient();
  const terminatedEntities = queryClient.getQueryData(
    entityKeys.terminatedEntities()
  ).data;

  const handleCheckedChange = (newCheckedEntityIDs) => {
    trackEvent("User checked a related entity");
    setCheckedEntityIDs(newCheckedEntityIDs);
  };

  const columns = showDetailedAffectedEntities
    ? getRelatedEntitiesTableColumns(
        selectedEntityTypeTab,
        entitiesByType,
        colors,
        terminatedEntities,
        hasMultipleSelectedTenants
      )
    : [];

  const searchedEntitiesByType = applySearchFilter(entitiesByType, searchText);

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Box display="flex" width="100%" justifyContent="space-between">
        <SearchTextField
          searchText={searchText}
          setSearchText={setSearchText}
        />
        <EntitiesActionButtons
          entitiesByType={entitiesByType}
          checkedEntityIDs={checkedEntityIDs}
          entityType={selectedEntityTypeTab}
        />
      </Box>
      <EntityTypeTabbedTable
        entitiesByType={searchedEntitiesByType}
        columns={columns}
        selectedEntityTypeTab={selectedEntityTypeTab}
        setSelectedEntityTypeTab={setSelectedEntityTypeTab}
        handleCheckedChange={handleCheckedChange}
      />
    </Box>
  );
}
